<template>
  <Panel class="ship-order-itinerary-panel">
    <template #header>
      <div class="ship-order-itinerary-panel-header">
        <span class="font-medium text-gray-darkest">
          {{
            t('shipments.shipmentNumParam', {
              param: shipment.id,
            })
          }}
        </span>

        <ShipmentChip
          v-if="shipment?.status && shipment?.health"
          :status="shipment.status"
          :health="shipment.health"
        />
      </div>
    </template>

    <template #icons>
      <router-link
        v-if="shipment.id"
        :to="{
          name: 'shipmentDetail',
          params: { shipmentId: shipment.id },
        }"
        class="details-link flex align-items-center"
      >
        {{ t('shipments.viewFullDetails') }}
        <i aria-hidden="true" class="pi pi-arrow-right" />
      </router-link>
    </template>

    <dl class="info">
      <div class="row-group">
        <div class="row">
          <dt>{{ t('carriers.carriers') }}</dt>
          <dd>
            {{ shipment.carrierName ?? '-' }}
            <template v-if="shipment.scac">({{ shipment.scac }})</template>
          </dd>
        </div>

        <div class="row">
          <dt>{{ t('common.proNum') }}</dt>
          <dd>{{ shipment.proNumber ?? '-' }}</dd>
        </div>
      </div>

      <div v-if="canReadShipmentLedger" class="row-group">
        <div
          v-if="
            shipmentFinancials.loading ||
            shipmentFinancials.data?.ledger?.events?.length
          "
          class="row"
        >
          <dt>{{ t('shipmentFinancials.cost') }}</dt>
          <dd>
            <LoadingIndicator v-if="shipmentFinancials.loading" loading />
            <span v-else>
              {{ renderMoney(shipmentFinancials.data?.ledger?.clientCost) }}
              {{ !shipmentFinancials.data?.payable ? '*' : '' }}
            </span>
          </dd>
        </div>
        <div v-else class="row">
          <dt>
            {{
              t('shipmentFinancials.paramEstimate', {
                param: t('shipmentFinancials.cost'),
              })
            }}
          </dt>
          <dd>
            {{
              renderMoney(shipmentFinancials.data?.jobCostingLedger?.clientCost)
            }}
          </dd>
        </div>
      </div>

      <div class="row-group">
        <div class="row">
          <dt>{{ t('shipments.modes') }}</dt>
          <dd>
            {{ shipment.mode ? translateEnum(shipment.mode, 'Mode') : '-' }}
          </dd>
        </div>

        <div class="row">
          <dt>{{ t('shipments.services') }}</dt>
          <dd>{{ shipment.service ? titleCase(shipment.service) : '-' }}</dd>
        </div>

        <div class="row">
          <dt>{{ t('common.mileage') }}</dt>
          <dd>{{ totalMileage ?? '-' }}</dd>
        </div>
      </div>
    </dl>

    <div class="locations">
      <KeyValuePair :label="t('orders.origin')" class="h-auto">
        <template #value>
          <ShipOrderItineraryLocation :location="shipment.origin.location" />
        </template>
      </KeyValuePair>

      <svg
        v-if="!!transportSegment"
        version="1.1"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        class="sequence-icon"
      >
        <circle cx="50" cy="50" r="48" />

        <text x="50" y="50" class="sequence-number">
          {{ transportSegment.sequence }}
        </text>
      </svg>
      <i v-else aria-hidden="true" class="pi pi-arrow-right text-gray-dark" />

      <KeyValuePair :label="t('orders.destination')" class="h-auto">
        <template #value>
          <ShipOrderItineraryLocation
            :location="shipment.destination.location"
          />
        </template>
      </KeyValuePair>
    </div>
  </Panel>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { renderMoney, titleCase, translateEnum } from '@rygen/rygen-ui/i18n';
import { Loadable } from '@rygen/rygen-ui/model';
import { ShipmentFinancials } from '@rygen/rygen-ui/model/shipmentFinancials';

import api from '@/api';
import { Shipment, ShipmentTransportSegment } from '@/model';

import ShipOrderItineraryLocation from '@/components/order/itinerary/ShipOrderItineraryLocation.vue';
import ShipmentChip from '@/components/shipment/ShipmentChip.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import Panel from 'primevue/panel';

const props = defineProps({
  shipment: {
    type: Object as PropType<Shipment>,
    required: true,
  },
  transportSegment: {
    type: Object as PropType<ShipmentTransportSegment>,
    default: () => undefined,
  },
});
const { shipment, transportSegment } = toRefs(props);

const { t } = useI18n();
const toast = useToast();

const shipmentFinancials: Ref<Loadable<ShipmentFinancials>> = ref({
  loading: false,
});

const canReadShipmentLedger = computed(() =>
  authorization.hasAuthority('shipmentFinancials', 'readShipmentLedger'),
);

const totalMileage = computed(() => {
  const mileage =
    shipment.value.quotedMileage ?? shipment.value.estimatedMileage;

  return mileage ? Math.round(mileage) : null;
});

const loadShipmentFinancials = async (shipmentId: number) => {
  shipmentFinancials.value.error = undefined;

  try {
    shipmentFinancials.value.loading = true;
    shipmentFinancials.value.data =
      await api.shipmentFinancials.getShipmentFinancialsById(shipmentId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    shipmentFinancials.value.error = e as Error;
    console.error('Error fetching shipment financials', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('shipments.financials.shipmentFinancials', 2),
      }),
      error: e,
    });
  } finally {
    shipmentFinancials.value.loading = false;
  }
};

watch(
  () => shipment.value.id,
  (newVal) => {
    if (newVal && canReadShipmentLedger.value) {
      loadShipmentFinancials(newVal);
    }
  },
  { immediate: true },
);
</script>

<style>
:root {
  --ship-order-itinerary-panel-background-color: var(--p-gray-200);
  /* border color should always be darker than ^ background color */
  --ship-order-itinerary-panel-border-color: var(--p-gray-300);
}

.ship-order-itinerary-panel.p-panel {
  --p-panel-background: var(--ship-order-itinerary-background-color);
  --p-panel-border-color: var(--ship-order-itinerary-panel-border-color);
  --p-panel-border-radius: 0;
  --p-panel-header-background: var(--ship-order-itinerary-background-color);
  --p-panel-header-border-radius: 0;
  --p-panel-header-border-width: 0 0 1px 0;
  --p-panel-header-padding: var(--sp-3) var(--sp-6) var(--sp-3) var(--sp-3);
  --p-panel-content-padding: 0;

  position: relative;
  border-width: 1px 0 0 0;

  .p-panel-header {
    border-inline: 0;

    .ship-order-itinerary-panel-header {
      display: flex;
      align-items: center;
      gap: var(--sp-3);
    }

    .details-link {
      gap: var(--sp-1);
    }
  }

  .p-panel-content {
    display: flex;
    flex-direction: row;

    .info {
      display: flex;
      flex-direction: column;
      background-color: var(--ship-order-itinerary-panel-background-color);
      margin: 0;
      width: 33.3333%;

      .row-group {
        padding: var(--sp-3) var(--sp-6);

        & + .row-group {
          border-top: 1px solid var(--ship-order-itinerary-panel-border-color);
        }

        .row {
          display: flex;
          justify-content: space-between;
          align-items: center;

          dt {
            flex: 1 0 40%;
            color: var(--p-gray-600);
            font-size: var(--label-font-size);
            font-weight: 500;
            text-transform: uppercase;
          }

          dd {
            flex: 1 0 60%;
            color: var(--p-gray-600);
            text-align: right;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin: 0;
          }
        }
      }
    }

    .locations {
      display: flex;
      justify-content: space-around;
      align-items: center;
      flex: 1;
      padding: var(--sp-6);

      .key-value-pair {
        flex-basis: max-content;

        .key-value-pair-label {
          font-size: 12px;
          text-transform: uppercase;
        }
      }

      .sequence-icon {
        width: 32px;
        height: 32px;

        circle {
          fill: var(--p-gray-300);
        }

        .sequence-number {
          font-size: 48px;
          font-weight: bold;
          dominant-baseline: central;
          text-anchor: middle;
          fill: var(--p-gray-600);
        }
      }
    }
  }
}
</style>
